import type { ContentRepository } from '@site/shared/ports'
import type { Locale } from '@/lib/i18n/messages'
import { createPayloadRepository } from '@site/cms-client'

/**
 * DI seam — single point that builds the concrete ContentRepository.
 * Swap implementations here (FastAPI, static, mock) without touching pages.
 *
 * VITE_CMS_URL is the PUBLIC CMS origin (baked in at build time): browsers
 * fetch from it and media URLs are absolutised against it. The SSR server may
 * reach the same API on a different (internal) URL — see
 * `createServerContentRepository`.
 */
export function publicCmsUrl(): string {
  const configured = (import.meta.env.VITE_CMS_URL as string | undefined) ?? ''
  if (configured) return configured
  // Unset in local dev: the Vite dev server proxies /api to the CMS.
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001'
}

export function createContentRepository(opts: {
  apiBaseUrl: string
  publicBaseUrl: string
  locale: Locale
}): ContentRepository {
  return createPayloadRepository(opts)
}

const browserRepos = new Map<Locale, ContentRepository>()

/** Browser-side repository for a locale (one instance per locale). */
export function getContentRepository(locale: Locale): ContentRepository {
  let repo = browserRepos.get(locale)
  if (!repo) {
    const base = publicCmsUrl()
    repo = createContentRepository({ apiBaseUrl: base, publicBaseUrl: base, locale })
    browserRepos.set(locale, repo)
  }
  return repo
}
